/**
 * As regras da ABNT que o documento precisa, em números.
 *
 * Tudo o que é medida, tamanho de letra ou ordem de elemento vive aqui, com a
 * fonte ao lado — NBR 14724 (apresentação de trabalhos), NBR 6022 (artigo),
 * NBR 6023 (referências), NBR 6024 (numeração progressiva), NBR 10520 (citações).
 *
 * **Por que no motor e não no desenho:** o documento tem de sair igual na prévia,
 * no PDF do navegador e no PDF do servidor. Se a medida morasse só no CSS, a
 * primeira correção de folha deixaria os três diferentes — e ninguém perceberia,
 * porque o papel esconde. Aqui os testes medem; o CSS obedece.
 */
import { blocoTemTexto, blocosDaSecao } from './blocos.js';
import { folhaDoDocumento, folhasDeAnotacao } from './folha.js';
import { normaNumerada, NIVEL_MAXIMO_DE_SECAO, papelDoDocumento } from './tipos.js';
/* ------------------------------------------------------------------ */
/* Folha, margens e letra                                              */
/* ------------------------------------------------------------------ */
/** Papel do documento: A4, sempre retrato. */
export const PAPEL_DOCUMENTO_MM = { largura: 210, altura: 297 };
/**
 * Margens da NBR 14724: 3 cm na esquerda e no topo, 2 cm na direita e no pé.
 *
 * A esquerda é maior de propósito — é o lado da encadernação.
 */
export const MARGENS_ABNT_MM = { esquerda: 30, topo: 30, direita: 20, fundo: 20 };
/** A mancha (área útil) que sobra das margens. */
export const MANCHA_DOCUMENTO_MM = {
    largura: PAPEL_DOCUMENTO_MM.largura - MARGENS_ABNT_MM.esquerda - MARGENS_ABNT_MM.direita,
    altura: PAPEL_DOCUMENTO_MM.altura - MARGENS_ABNT_MM.topo - MARGENS_ABNT_MM.fundo,
};
/** Corpo do texto a 12 pt, entrelinha de 1,5 — o padrão da norma. */
export const TEXTO_DOCUMENTO = { tamanhoPt: 12, entrelinha: 1.5 };
/** Citações longas, notas, legendas e fontes: 10 pt, espaço simples. */
export const TEXTO_REDUZIDO = { tamanhoPt: 10, entrelinha: 1 };
/** Recuo da primeira linha do parágrafo. A norma aceita 1,25 cm ou 1,5 cm. */
export const RECUO_DE_PARAGRAFO_MM = 12.5;
/** Citação com mais de três linhas: recuo de 4 cm da margem esquerda, sem aspas. */
export const RECUO_DE_CITACAO_LONGA_MM = 40;
/**
 * O degrau do recuo de bloco ("aumentar recuo"/"diminuir recuo"): 12,5 mm.
 *
 * É o mesmo valor do recuo de primeira linha do parágrafo — e o mesmo passo do
 * "Aumentar recuo" de um processador de texto comum (1,25 cm). Um degrau por
 * clique mantém o texto na grade da mancha: nada de deslocamento miúdo, que
 * ninguém consegue repetir à mão no dia seguinte.
 */
export const DEGRAU_DE_RECUO_MM = RECUO_DE_PARAGRAFO_MM;
/**
 * A linha mais estreita que um recuo pode deixar.
 *
 * O recuo é escolha de quem escreve, mas o papel não estica: passando disto a
 * coluna fica impraticável (três palavras por linha) e o parágrafo vira um risco
 * no meio da folha. Quem decide o teto é a mancha do papel, em
 * `degrausDeRecuoDoPapel`.
 */
export const LINHA_MINIMA_DO_RECUO_MM = 60;
/**
 * Espaço entre o título e o texto nas **folhas de abertura** (resumo, sumário,
 * listas): ali o título é um cabeçalho de folha inteira, e não o título de uma
 * seção, que se separa do texto por `espacoDeUmaLinhaMm`.
 */
export const ESPACO_APOS_TITULO_MM = 6;
/**
 * Uma linha do texto, em milímetros.
 *
 * É o que a norma chama de "espaço de 1,5": o que separa um título do que vem antes e
 * depois dele é **uma linha do corpo** — não um valor escolhido a dedo, que ficaria
 * errado assim que o papel ou o corpo mudassem (o A5 tem corpo 11 e entrelinha 1,45;
 * o A4, 12 e 1,5). Saem também daqui os parágrafos no formato simples.
 */
export function espacoDeUmaLinhaMm(geometria) {
    return Math.round(((geometria.corpoPt * geometria.entrelinha * 25.4) / 72) * 100) / 100;
}
export const GEOMETRIA_DE_PAPEL = {
    A4: {
        papel: 'A4',
        larguraMm: PAPEL_DOCUMENTO_MM.largura,
        alturaMm: PAPEL_DOCUMENTO_MM.altura,
        margens: { ...MARGENS_ABNT_MM },
        larguraManchaMm: MANCHA_DOCUMENTO_MM.largura,
        alturaManchaMm: MANCHA_DOCUMENTO_MM.altura,
        corpoPt: TEXTO_DOCUMENTO.tamanhoPt,
        entrelinha: TEXTO_DOCUMENTO.entrelinha,
        reduzidoPt: TEXTO_REDUZIDO.tamanhoPt,
        recuoParagrafoMm: RECUO_DE_PARAGRAFO_MM,
        recuoCitacaoMm: RECUO_DE_CITACAO_LONGA_MM,
    },
    A5: {
        papel: 'A5',
        larguraMm: 148,
        alturaMm: 210,
        margens: { esquerda: 20, topo: 20, direita: 15, fundo: 15 },
        larguraManchaMm: 148 - 20 - 15,
        alturaManchaMm: 210 - 20 - 15,
        corpoPt: 11,
        entrelinha: 1.45,
        reduzidoPt: 10,
        recuoParagrafoMm: 10,
        recuoCitacaoMm: 24,
    },
};
/** A geometria do papel pedido (A4 quando não se diz nada). */
export function geometriaDoPapel(papel = 'A4') {
    return GEOMETRIA_DE_PAPEL[papel] ?? GEOMETRIA_DE_PAPEL.A4;
}
/**
 * As margens que a pessoa (ou o padrão da instituição) escolheu, encaixadas no
 * que o papel aceita.
 *
 * A norma fixa 3-2-3-2 cm, mas o manual de cada faculdade tem o seu número — e é
 * por isso que a margem é um campo do documento. O que **não** pode acontecer é
 * a mancha sumir: margem negativa, margem do tamanho do papel ou combinação que
 * deixe menos de `MANCHA_MINIMA_MM` de largura. Nesse caso o valor pedido é
 * descartado e vale o da norma, em silêncio — o site mostra o número que está
 * valendo, então quem escolheu vê a diferença.
 */
export const MANCHA_MINIMA_MM = 80;
export function margensDoDocumento(pedido, papel = 'A4') {
    const base = { ...geometriaDoPapel(papel).margens };
    if (!pedido)
        return base;
    const numero = (valor, padrao) => {
        const n = Number(valor);
        return Number.isFinite(n) && n >= 0 ? Math.min(n, 100) : padrao;
    };
    const escolhidas = {
        esquerda: numero(pedido.esquerda, base.esquerda),
        topo: numero(pedido.topo, base.topo),
        direita: numero(pedido.direita, base.direita),
        fundo: numero(pedido.fundo, base.fundo),
    };
    const { larguraMm, alturaMm } = geometriaDoPapel(papel);
    const sobraLargura = larguraMm - escolhidas.esquerda - escolhidas.direita;
    const sobraAltura = alturaMm - escolhidas.topo - escolhidas.fundo;
    if (sobraLargura < MANCHA_MINIMA_MM || sobraAltura < MANCHA_MINIMA_MM)
        return base;
    return escolhidas;
}
/**
 * A geometria do documento, com o que a pessoa escolheu por cima: corpo do texto,
 * margens e recuo do parágrafo.
 *
 * Livro em A5 nasce com corpo 11 e, para quem lê com dificuldade, é pouco: o
 * painel deixa aumentar o corpo. O texto reduzido (citação, legenda, referência)
 * acompanha a **mesma diferença**, para a hierarquia não quebrar — se o corpo sobe
 * dois pontos, o reduzido sobe dois também.
 *
 * As margens vêm do padrão da instituição quando ele as define (o Centro Paula
 * Souza, por exemplo, pede as mesmas da norma — mas o manual dele diz 3-3-2-2 e é
 * o manual que manda no trabalho do aluno).
 */
export function geometriaDoDocumento(documento) {
    const base = geometriaDoPapel(documento.papel ?? 'A4');
    const margens = margensDoDocumento(documento.margensMm, documento.papel ?? 'A4');
    const corpoPt = documento.corpoDoTextoPt ?? base.corpoPt;
    const reduzidoPt = corpoPt === base.corpoPt ? base.reduzidoPt : Math.max(8, base.reduzidoPt + (corpoPt - base.corpoPt));
    /*
     * `null` é "não escolhi" — e `Number(null)` dá 0, que passaria no teste de
     * número finito e zeraria o recuo em silêncio. Por isso o `null` vira `NaN`
     * antes da conta.
     */
    const recuoBruto = documento.recuoDoParagrafoMm;
    const recuoPedido = recuoBruto === null || recuoBruto === undefined ? Number.NaN : Number(recuoBruto);
    const recuoParagrafoMm = Number.isFinite(recuoPedido) && recuoPedido >= 0 && recuoPedido <= 50
        ? recuoPedido
        : base.recuoParagrafoMm;
    const larguraManchaMm = base.larguraMm - margens.esquerda - margens.direita;
    const alturaManchaMm = base.alturaMm - margens.topo - margens.fundo;
    const semMudanca = corpoPt === base.corpoPt &&
        recuoParagrafoMm === base.recuoParagrafoMm &&
        margens.esquerda === base.margens.esquerda &&
        margens.topo === base.margens.topo &&
        margens.direita === base.margens.direita &&
        margens.fundo === base.margens.fundo;
    if (semMudanca)
        return base;
    return {
        ...base,
        margens,
        larguraManchaMm,
        alturaManchaMm,
        corpoPt,
        reduzidoPt,
        recuoParagrafoMm,
    };
}
/**
 * Quantos degraus de recuo o papel aceita.
 *
 * O A4 (mancha de 160 mm) aceita oito; o A5, quatro — a conta é a linha que sobra
 * depois do recuo, e não um número escolhido a dedo. Passar disto não é recusado
 * em silêncio: o desenho, a barra do editor e os testes leem o mesmo teto daqui.
 */
export function degrausDeRecuoDoPapel(papel = 'A4') {
    const sobra = geometriaDoPapel(papel).larguraManchaMm - LINHA_MINIMA_DO_RECUO_MM;
    return Math.max(0, Math.floor(sobra / DEGRAU_DE_RECUO_MM));
}
/** O recuo pedido, encaixado no que o papel aceita: um número inteiro de degraus. */
export function degrauDeRecuo(degrau, papel = 'A4') {
    const pedido = Math.round(Number.isFinite(degrau) ? Number(degrau) : 0);
    return Math.min(Math.max(pedido, 0), degrausDeRecuoDoPapel(papel));
}
/**
 * O recuo de um bloco, em milímetros: o que o papel e a prévia desenham.
 *
 * Degrau ausente ou zero devolve zero — é o bloco na margem, como sempre foi
 * impresso antes deste recurso existir.
 */
export function recuoDeBlocoMm(degrau, papel = 'A4') {
    return degrauDeRecuo(degrau, papel) * DEGRAU_DE_RECUO_MM;
}
/** O afastamento mínimo do número de página das bordas (canto superior direito). */
export const MARGEM_DA_PAGINACAO_MM = { topo: 10, direita: 20 };
/* ------------------------------------------------------------------ */
/* Folhas de anotação                                                  */
/* ------------------------------------------------------------------ */
/** A pauta da folha de anotação: 8 mm, como um caderno grande. */
export const ESPACAMENTO_DA_PAUTA_MM = 8;
/** A grade de bolinhas: mais fechada, para escrita miúda e desenho. */
export const ESPACAMENTO_DA_GRADE_MM = 6;
/** O respiro no alto da folha, onde o título "Anotações" é impresso. */
export const RESPIRO_DA_ANOTACAO_MM = 12;
/**
 * A margem da folha de anotação: estreita e **igual nos quatro lados**.
 *
 * A folha de anotação não é página de texto — é a **folha inteira** para escrever.
 * As margens da norma (30 mm à esquerda, 20 à direita no A4) existem para a mancha
 * do texto e cortariam justamente a área onde a pessoa anota: com elas, metade da
 * folha sairia vazia. Por isso a pauta usa esta margem, e o site declara uma
 * **página nomeada** com ela (`PAGINA_DA_ANOTACAO`), para a folha toda ser da
 * pauta. Nas folhas de anotação não saem cabeçalho, rodapé nem número: é folha de
 * caderno.
 */
export const MARGEM_DA_ANOTACAO_MM = 12;
/**
 * A folha de anotação medida para o papel do documento.
 *
 * A medida é a da **folha inteira** menos a margem própria da anotação
 * (`MARGEM_DA_ANOTACAO_MM`) — quem escreve à mão precisa da folha, não da mancha
 * do texto. O ponto delicado continua sendo a **altura**: uma folha de anotação
 * que passe um milímetro da página empurra a próxima para uma folha extra em
 * branco, e o livro sai com uma folha fantasma a cada anotação. Por isso a conta
 * aqui é inteira — quantas linhas cabem e quanto elas somam —, e nunca "cobre o
 * resto da página".
 */
export function pautaDaAnotacao(geometria, formato = 'LINHAS') {
    const larguraMm = Math.max(0, geometria.larguraMm - 2 * MARGEM_DA_ANOTACAO_MM);
    const alturaDaPaginaMm = Math.max(0, geometria.alturaMm - 2 * MARGEM_DA_ANOTACAO_MM);
    if (formato === 'BRANCA') {
        return {
            formato,
            espacamentoMm: 0,
            linhas: 0,
            colunas: 0,
            respiroMm: 0,
            larguraMm,
            alturaMm: alturaDaPaginaMm,
        };
    }
    const espacamentoMm = formato === 'PONTOS' ? ESPACAMENTO_DA_GRADE_MM : ESPACAMENTO_DA_PAUTA_MM;
    const respiroMm = RESPIRO_DA_ANOTACAO_MM;
    const disponivel = alturaDaPaginaMm - respiroMm;
    const linhas = Math.max(0, Math.floor(disponivel / espacamentoMm));
    return {
        formato,
        espacamentoMm,
        linhas,
        colunas: Math.floor(larguraMm / espacamentoMm),
        respiroMm,
        larguraMm,
        alturaMm: respiroMm + linhas * espacamentoMm,
    };
}
/**
 * O rótulo com que o texto pode anunciar uma seção numerada: "CAPÍTULO 4", "PARTE II".
 *
 * É o mesmo que o leitor do texto colado reconhece — mas aqui o número digitado
 * **sai**, porque quem numera é o gerador.
 */
const ROTULO_NUMERADO = /^(?:cap[íi]tulo|parte|seç[ãa]o|secao|t[íi]tulo|titulo)\s+(?:[0-9]+|[ivxlcdm]+)\s*[.)\-–—:]?\s*/i;
/**
 * O título é **só o nome de exemplo do capítulo** — "Capítulo 1", "Capítulo III"?
 *
 * É o nome com que o livro nasce ("Capítulo 1", "Capítulo 2"): um lugar onde
 * escrever, e não um título. O livro anuncia o capítulo pelo número, então deixá-lo
 * sair no papel imprimia "1. CAPÍTULO 1" — a palavra que o livro não usa, repetida
 * ao lado do número.
 *
 * Diferente de `ROTULO_NUMERADO`, aqui o rótulo tem de ser **tudo** o que está
 * escrito: "Capítulo 4 — O mar" continua sendo título (o texto depois do rótulo é o
 * nome), e quem o separa é `tituloSemNumero`.
 */
const SO_ROTULO_DE_CAPITULO = /^cap[íi]tulo\s+(?:[0-9]+|[ivxlcdm]+)\s*[.)\-–—:]?\s*$/i;
/**
 * A numeração digitada no começo do título: "4. texto", "4.1 Método", "IV. Capítulo".
 *
 * O **separador** (ponto, parêntese, traço ou dois-pontos) é o que separa o número
 * digitado do título que apenas **começa** com número: "100 anos de solidão" é título e
 * continua inteiro, "100. anos de solidão" é o item 100. Sem exigir o separador, o
 * gerador comeria o número de um livro de verdade — e quem confere é o sumário.
 */
const NUMERO_DIGITADO = /^(?:[0-9]+(?:\.[0-9]+)+|[0-9]+[.)\-–—:]|[ivxlcdm]+[.)])[ \t]+/i;
/**
 * O título sem o número (e sem o rótulo) que a pessoa digitou.
 *
 * O motor numera por conta própria, pela ordem das seções: o "4." que veio escrito no
 * título sai, e o capítulo recebe o número que **é** o dele — trocar duas seções de
 * lugar renumera as duas, e o número digitado à mão não fica mentindo. O sumário
 * acompanha, porque tira o título daqui.
 *
 * Título que era **só** o número ("4.") fica como está: tirar tudo deixaria a seção sem
 * nome, e sem título o gerador avisa que falta. Aqui é o texto que manda.
 */
export function tituloSemNumero(titulo) {
    const limpo = titulo.trim();
    const semRotulo = limpo.replace(ROTULO_NUMERADO, '');
    const semNumero = (semRotulo === limpo ? limpo.replace(NUMERO_DIGITADO, '') : semRotulo).trim();
    return semNumero || limpo;
}
/**
 * Numera as seções: `1`, `1.1`, `1.1.1` na norma, `1.`, `1.1.`, `1.1.1.` no livro.
 *
 * O contador de cada nível zera quando o nível de cima avança — "1.2" sempre vem
 * depois de "1.1", e a primeira seção de um nível novo volta ao um. O título
 * primário sai em **maiúsculas** em todos os modelos (no trabalho e no artigo porque a
 * norma pede; no livro porque é a regra do modelo), e é daqui que o sumário também
 * tira os títulos: escrever a caixa em um lugar só é o que impede a folha e o sumário
 * de discordarem.
 */
export function numerarSecoes(secoes, opcoes = {}) {
    /* Um contador por nível: a numeração desce até o teto (o editor oferece até o 6). */
    const contador = Array.from({ length: NIVEL_MAXIMO_DE_SECAO }, () => 0);
    const livro = Boolean(opcoes.livro);
    return secoes.map((secao) => {
        const indice = secao.nivel - 1;
        contador[indice] = (contador[indice] ?? 0) + 1;
        for (let depois = indice + 1; depois < contador.length; depois += 1)
            contador[depois] = 0;
        const titulo = tituloSemNumero(secao.titulo);
        /*
         * Nível sem seção acima conta como **1**: um "1.1" solto — sem o "1" antes — é o
         * que a norma não admite, e o gerador numera como se a seção de cima existisse.
         * Sem isto sairia "0.1" no papel, que não quer dizer nada (o aviso de hierarquia
         * faltando é dado por `avisosDoDocumento`).
         */
        const numeros = contador.slice(0, indice + 1).map((valor) => Math.max(1, valor));
        if (livro) {
            /*
             * O **nome de exemplo** do capítulo não imprime: o livro anuncia o capítulo
             * pelo número, e o "Capítulo 1" com que ele nasce é onde o texto do capítulo
             * se escreve, não um título. Sem isto, quem escrevia o capítulo sem renomear
             * imprimia "1. CAPÍTULO 1" — a palavra que o livro não usa, ao lado do número.
             *
             * O nome continua guardado no documento (é o que o painel dos capítulos
             * mostra, e o que a pessoa troca quando quiser): muda só o que sai na folha.
             */
            const nomeDoCapitulo = secao.nivel === 1 && SO_ROTULO_DE_CAPITULO.test(secao.titulo.trim()) ? '' : titulo;
            return {
                secao,
                /* O ponto depois do último algarismo: é assim que o livro escreve o título. */
                numero: `${numeros.join('.')}.`,
                /*
                 * O título do capítulo em caixa alta; as partes de dentro saem como foram
                 * escritas (quem escreve decide a caixa do que está dentro do capítulo).
                 */
                titulo: secao.nivel === 1 ? nomeDoCapitulo.toUpperCase() : nomeDoCapitulo,
                nivel: secao.nivel,
                capitulo: secao.nivel === 1,
                ordinal: contador[0] ?? 1,
            };
        }
        return {
            secao,
            numero: numeros.join('.'),
            titulo: secao.nivel === 1 ? titulo.toUpperCase() : titulo,
            nivel: secao.nivel,
            capitulo: false,
            ordinal: contador[0] ?? 1,
        };
    });
}
/**
 * A seção tem o que imprimir: o texto dela **ou** o de uma subseção dela.
 *
 * Uma seção com título e **sem texto** não sai no papel (um título solto não é um
 * capítulo) — é assim que um documento novo, cheio de seções vazias, não imprime uma
 * lista de títulos pelo caminho. Mas o conteúdo de uma seção **inclui as subseções
 * dela**: um capítulo cujo texto está todo nas partes de dentro (a "Apresentação", o
 * "Tema para estudo"…) tem o que imprimir, e é o título dele que abre o capítulo na
 * folha e no sumário.
 *
 * Sem esta conta o capítulo sumia — e a numeração pulava (1, 2, 3, **5**), porque o
 * capítulo sem texto continuava contando. Foi o defeito visto num livro dividido em
 * capítulos e partes: "O Espírito Santo" (nível 1, sem parágrafo próprio, com sete
 * partes cheias de texto) não aparecia em lugar nenhum, e o capítulo seguinte saía
 * como 5.
 */
export function temConteudoImpresso(secoes, indice) {
    const secao = secoes[indice];
    if (!secao)
        return false;
    if (blocosDaSecao(secao).some(blocoTemTexto))
        return true;
    /*
     * As subseções desta: as que vêm depois, até a próxima seção de nível igual ou
     * menor (a partir dali elas são de outro dono).
     */
    for (let seguinte = indice + 1; seguinte < secoes.length; seguinte += 1) {
        const outra = secoes[seguinte];
        if (outra.nivel <= secao.nivel)
            return false;
        if (blocosDaSecao(outra).some(blocoTemTexto))
            return true;
    }
    return false;
}
/* ------------------------------------------------------------------ */
/* Referências (NBR 6023)                                              */
/* ------------------------------------------------------------------ */
/**
 * Palavras que denunciam **entidade**, e não pessoa: órgão, empresa, escola.
 *
 * A norma escreve autor institucional com o nome todo em maiúsculas, sem
 * inverter nada — "MINISTÉRIO DA EDUCAÇÃO", e não "DA EDUCAÇÃO, Ministério".
 * Sem esta lista, quem cadastrava um órgão como autor recebia uma referência com
 * cara de pessoa.
 */
const PALAVRAS_DE_ENTIDADE = [
    'ministério',
    'secretaria',
    'universidade',
    'instituto',
    'prefeitura',
    'associação',
    'organização',
    'fundação',
    'escola',
    'faculdade',
    'conselho',
    'comissão',
    'governo',
    'câmara',
    'tribunal',
    'departamento',
    'serviço',
    'sociedade',
    'programa',
    'projeto',
    'agência',
    'hospital',
    'centro',
    'núcleo',
    'sindicato',
    'cooperativa',
    'empresa',
    'banco',
];
/** Sufixos que andam junto do sobrenome: "SILVA JÚNIOR, João". */
const SUFIXOS_DE_NOME = new Set(['júnior', 'junior', 'filho', 'neto', 'sobrinho', 'segundo']);
/**
 * Partículas **estrangeiras** que fazem parte do sobrenome: "VAN DER BERG".
 *
 * As portuguesas (de, da, do, das, dos, e) ficam com o **prenome**, como a norma
 * escreve: "SOUZA, Maria de".
 */
/** Partículas que ficam junto do sobrenome ("van Gogh", "de la Cruz"). */
export const PARTICULAS_ESTRANGEIRAS = new Set([
    'van',
    'von',
    'der',
    'den',
    'del',
    'della',
    'delle',
    'di',
    'la',
    'le',
    'du',
    'st',
    'mac',
    'mc',
]);
function ehEntidade(nome) {
    const primeira = nome.split(' ')[0]?.toLowerCase() ?? '';
    return PALAVRAS_DE_ENTIDADE.includes(primeira);
}
/**
 * Ajeita a caixa do prenome: "ana souza" vira "SOUZA, Ana".
 *
 * Quem preenche o nome num formulário costuma escrever tudo em minúsculas (ou
 * tudo em maiúsculas), e a norma imprime o prenome **como ele se escreve**. Sem
 * isto, a referência saía "SOUZA, ana" no papel — e ninguém revisa a lista de
 * referências procurando isso.
 *
 * As partículas ficam como estão ("de", "da", "dos") e as iniciais continuam em
 * maiúsculas, com o ponto: "J. R. R." não vira "J. r. r.".
 */
function capitalizarPrenome(prenome) {
    const particulas = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
    return prenome
        .split(' ')
        .map((palavra) => {
        if (!palavra)
            return palavra;
        if (particulas.has(palavra.toLowerCase()))
            return palavra.toLowerCase();
        /*
         * Inicial ou sigla pontuada: "J." e "J.R.R." continuam em maiúsculas com os
         * pontos. O teste é a **letra solta ou o ponto** — e não o tamanho da
         * palavra: por tamanho, "ANA" (três letras, tudo maiúsculo) seria tratada
         * como sigla e a referência sairia "SOUZA, ANA Maria".
         */
        if (/^[A-Za-zÀ-Ü]\.?$/.test(palavra) || palavra.includes('.')) {
            return palavra.toUpperCase();
        }
        return palavra.charAt(0).toUpperCase() + palavra.slice(1).toLowerCase();
    })
        .join(' ');
}
/**
 * "SILVA, João" a partir de "João Silva".
 *
 * A norma pede o **sobrenome em maiúsculas**, seguido do prenome. Quem digitar já
 * no formato certo não é mexido; autor institucional sai inteiro em maiúsculas; e
 * o nome com sufixo ("João Silva Júnior") mantém o sufixo junto do sobrenome.
 */
export function nomeNaReferencia(nome) {
    const limpo = nome.trim().replace(/\s+/g, ' ');
    if (!limpo)
        return '';
    // Autor institucional: tudo em maiúsculas, sem inversão.
    if (ehEntidade(limpo))
        return limpo.toUpperCase();
    // Já está no formato da norma: tem vírgula.
    if (limpo.includes(',')) {
        const [sobrenome, prenome] = limpo.split(',', 2);
        return prenome?.trim() ? `${sobrenome.trim()}, ${capitalizarPrenome(prenome.trim())}` : sobrenome.trim();
    }
    const partes = limpo.split(' ');
    if (partes.length === 1)
        return limpo.toUpperCase();
    let corte = partes.length - 1;
    if (SUFIXOS_DE_NOME.has(partes[corte].toLowerCase()) && corte > 0)
        corte -= 1;
    while (corte > 0 && PARTICULAS_ESTRANGEIRAS.has(partes[corte - 1].toLowerCase()))
        corte -= 1;
    const sobrenome = partes.slice(corte).join(' ');
    const prenome = partes.slice(0, corte).join(' ');
    return prenome ? `${sobrenome.toUpperCase()}, ${capitalizarPrenome(prenome)}` : sobrenome.toUpperCase();
}
/** Junta os autores: "SILVA, João; SOUZA, Ana" — ponto e vírgula, como a norma pede. */
export function autoresNaReferencia(autores) {
    return autores
        .map(nomeNaReferencia)
        .filter(Boolean)
        .join('; ');
}
/**
 * O veículo destacado: `Folha de S.Paulo, São Paulo` vira `**Folha de S.Paulo**, São Paulo`.
 *
 * A NBR 6023 destaca o **nome do periódico**, e a cidade vem depois — por isso o corte na
 * primeira vírgula, que é o que separa um do outro quando a pessoa escreve os dois no mesmo
 * campo.
 */
function veiculoDestacado(local) {
    const limpo = local.trim();
    if (!limpo)
        return '';
    const [nome, ...resto] = limpo.split(',');
    if (!nome?.trim())
        return '';
    const destacado = `**${nome.trim()}**`;
    return resto.length ? `${destacado},${resto.join(',')}` : destacado;
}
/**
 * Quando a edição saiu: o **dia** quando ele existe, o ano quando não.
 *
 * Com a data na mão sai `12 out. 2026` — que é o que o jornal exige; sem ela sai o ano, e
 * quem já tem a referência gravada continua com a linha de antes (o campo é opcional).
 */
function quandoSaiu(referencia, antes = '') {
    const data = referencia.dataPublicacao?.trim();
    const ano = referencia.ano?.trim();
    if (data)
        return `${antes}${dataNaReferencia(data)}.`;
    return ano ? `${antes}${ano}.` : '.';
}
/** O que a NBR escreve como suporte quando a obra não é papel: `1 vídeo (12 min)`. */
const SUPORTE_DA_MIDIA = {
    VIDEO: 'vídeo',
    AUDIO: 'arquivo de áudio',
    FILME: 'filme',
    IMAGEM: 'imagem',
};
function suporteDaMidia(referencia) {
    const unidade = SUPORTE_DA_MIDIA[referencia.tipo];
    if (!unidade)
        return '';
    const duracao = referencia.duracao?.trim();
    return duracao ? `1 ${unidade} (${duracao})` : `1 ${unidade}`;
}
/**
 * A referência formatada, pronta para imprimir.
 *
 * Cobre os tipos que o editor oferece, e o que não foi preenchido simplesmente não aparece
 * — sem "s.d." inventado, sem vírgula solta.
 */
export function referenciaFormatada(referencia) {
    /*
     * Referência do tipo `LIVRE`: sai como a pessoa escreveu. É a lista pronta,
     * colada de outro trabalho — reescrever aqui seria adivinhar campos que não
     * existem, e a banca confere a lista linha por linha.
     */
    if (referencia.tipo === 'LIVRE')
        return (referencia.texto ?? '').trim();
    const autores = autoresNaReferencia(referencia.autores);
    const titulo = [referencia.titulo.trim(), referencia.subtitulo?.trim()]
        .filter(Boolean)
        .join(': ');
    const edicao = referencia.edicao?.trim() ? `${referencia.edicao.trim()}. ed. ` : '';
    const ano = referencia.ano?.trim();
    const partes = [];
    switch (referencia.tipo) {
        case 'ARTIGO': {
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`${titulo}. `);
            partes.push(`${referencia.local.trim()}`);
            if (referencia.volume?.trim())
                partes.push(`, v. ${referencia.volume.trim()}`);
            if (referencia.numero?.trim())
                partes.push(`, n. ${referencia.numero.trim()}`);
            if (referencia.paginas?.trim())
                partes.push(`, p. ${referencia.paginas.trim()}`);
            partes.push(ano ? `, ${ano}.` : '.');
            break;
        }
        case 'SITE': {
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`${titulo}. `);
            if (referencia.local?.trim())
                partes.push(`${referencia.local.trim()}, `);
            partes.push(ano ? `${ano}. ` : '');
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}. `);
            partes.push(referencia.acessoEm?.trim() ? `Acesso em: ${dataNaReferencia(referencia.acessoEm)}.` : '');
            break;
        }
        case 'CAPITULO': {
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`${titulo}. `);
            partes.push(`In: ${referencia.local.trim()}. `);
            partes.push(edicao);
            if (referencia.paginas?.trim())
                partes.push(`p. ${referencia.paginas.trim()}. `);
            partes.push(`${referencia.local.trim()}: ${referencia.editora?.trim() || 'sem editora'}, ${ano}.`);
            break;
        }
        case 'TRABALHO': {
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`**${titulo}**. `);
            partes.push(ano ? `${ano}. ` : '');
            if (referencia.instituicao?.trim())
                partes.push(`${referencia.instituicao.trim()}, `);
            partes.push(`${referencia.local.trim()}.`);
            break;
        }
        case 'JORNAL': {
            /*
             * Reportagem de jornal: a base é a do artigo, mas o que identifica a edição é o
             * **dia** ("12 out. 2026") e o caderno vem **depois** dele, como a NBR escreve.
             */
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`${titulo}. `);
            const jornal = veiculoDestacado(referencia.local);
            if (jornal)
                partes.push(`${jornal}, `);
            partes.push(quandoSaiu(referencia));
            if (referencia.secao?.trim())
                partes.push(` ${referencia.secao.trim()}`);
            if (referencia.paginas?.trim()) {
                partes.push(`${referencia.secao?.trim() ? ',' : ''} p. ${referencia.paginas.trim()}.`);
            }
            if (referencia.url?.trim())
                partes.push(` Disponível em: ${referencia.url.trim()}.`);
            if (referencia.acessoEm?.trim()) {
                partes.push(` Acesso em: ${dataNaReferencia(referencia.acessoEm)}.`);
            }
            break;
        }
        case 'REVISTA': {
            /* A revista traz edição, volume e número **antes** das páginas, e a data no fim. */
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`${titulo}. `);
            const revista = veiculoDestacado(referencia.local);
            if (revista)
                partes.push(`${revista}, `);
            if (referencia.edicao?.trim())
                partes.push(`ed. ${referencia.edicao.trim()}, `);
            if (referencia.volume?.trim())
                partes.push(`v. ${referencia.volume.trim()}, `);
            if (referencia.numero?.trim())
                partes.push(`n. ${referencia.numero.trim()}, `);
            if (referencia.paginas?.trim())
                partes.push(`p. ${referencia.paginas.trim()}, `);
            partes.push(quandoSaiu(referencia));
            if (referencia.url?.trim())
                partes.push(` Disponível em: ${referencia.url.trim()}.`);
            if (referencia.acessoEm?.trim()) {
                partes.push(` Acesso em: ${dataNaReferencia(referencia.acessoEm)}.`);
            }
            break;
        }
        case 'EVENTO': {
            /*
             * Trabalho apresentado em evento: o que entra no lugar do autor é o **evento** (com a
             * edição e o ano), e a obra que reúne os trabalhos é sempre os *Anais*.
             */
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`${titulo}. `);
            const evento = referencia.local.trim();
            const edicaoDoEvento = referencia.numero?.trim();
            partes.push(`In: ${evento ? `**${evento}**` : ''}`);
            if (edicaoDoEvento)
                partes.push(`, ${edicaoDoEvento}.,`);
            partes.push(ano ? `${edicaoDoEvento ? ' ' : ', '}${ano}. ` : '. ');
            /*
             * A obra que reúne os trabalhos do evento é sempre os *Anais* — escrito por aqui, e não
             * como "[...]", porque o gerador não preenche os reticências que a norma usa para dizer
             * "o título completo dos anais vem aqui".
             */
            partes.push('**Anais do evento**. ');
            if (referencia.editora?.trim())
                partes.push(`${referencia.editora.trim()}, `);
            partes.push(ano ? `${ano}. ` : '');
            if (referencia.paginas?.trim())
                partes.push(`p. ${referencia.paginas.trim()}. `);
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}. `);
            partes.push(referencia.acessoEm?.trim() ? `Acesso em: ${dataNaReferencia(referencia.acessoEm)}.` : '');
            break;
        }
        case 'LEGISLACAO': {
            /*
             * Lei, decreto, portaria ou norma técnica.
             *
             * Duas formas, e o campo **Publicação oficial** escolhe entre elas: com ele a linha sai
             * como a NBR escreve a legislação (veículo oficial, cidade, seção, página, data); sem
             * ele, como a norma técnica, que se parece com um livro — cidade, editora, ano.
             */
            partes.push(autores ? `${autores}. ` : '');
            partes.push(referencia.titulo.trim() ? `${referencia.titulo.trim()}. ` : '');
            if (referencia.subtitulo?.trim())
                partes.push(`${referencia.subtitulo.trim()}. `);
            const veiculo = referencia.veiculo?.trim();
            if (veiculo) {
                partes.push(veiculo);
                if (referencia.local?.trim())
                    partes.push(`: ${referencia.local.trim()}`);
                if (referencia.secao?.trim())
                    partes.push(`, ${referencia.secao.trim()}`);
                if (referencia.paginas?.trim())
                    partes.push(`, p. ${referencia.paginas.trim()}`);
                partes.push(quandoSaiu(referencia, ', '));
            }
            else {
                if (referencia.local?.trim())
                    partes.push(referencia.local.trim());
                if (referencia.editora?.trim())
                    partes.push(`: ${referencia.editora.trim()}`);
                partes.push(ano ? `, ${ano}.` : '.');
            }
            if (referencia.url?.trim())
                partes.push(` Disponível em: ${referencia.url.trim()}.`);
            if (referencia.acessoEm?.trim()) {
                partes.push(` Acesso em: ${dataNaReferencia(referencia.acessoEm)}.`);
            }
            break;
        }
        case 'VIDEO':
        case 'AUDIO':
        case 'FILME':
        case 'IMAGEM': {
            /*
             * O que não é papel: o suporte e o tempo entram na linha ("1 vídeo (12 min)"), e o
             * endereço e a data de acesso seguem o que a NBR pede para o que está na internet.
             */
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`**${titulo}**. `);
            const produtora = [referencia.veiculo?.trim(), referencia.editora?.trim()]
                .filter(Boolean)
                .join(', ');
            if (referencia.local?.trim() || produtora) {
                partes.push(`${referencia.local?.trim() || '[S.l.]'}: `);
                partes.push(`${produtora}${ano ? `, ${ano}. ` : '. '}`);
            }
            else {
                partes.push(ano ? `${ano}. ` : '');
            }
            const suporte = suporteDaMidia(referencia);
            if (suporte)
                partes.push(`${suporte}. `);
            if (referencia.url?.trim())
                partes.push(`Disponível em: ${referencia.url.trim()}. `);
            partes.push(referencia.acessoEm?.trim() ? `Acesso em: ${dataNaReferencia(referencia.acessoEm)}.` : '');
            break;
        }
        case 'LIVRO':
        default: {
            partes.push(autores ? `${autores}. ` : '');
            partes.push(`**${titulo}**. `);
            partes.push(edicao);
            partes.push(`${referencia.local.trim()}`);
            if (referencia.editora?.trim())
                partes.push(`: ${referencia.editora.trim()}`);
            partes.push(ano ? `, ${ano}.` : '.');
            break;
        }
    }
    return partes
        .join('')
        .replace(/\s+\./g, '.')
        .replace(/\.\./g, '.')
        .replace(/\s+/g, ' ')
        .replace(/\.$/, '.')
        .trim();
}
/** `2026-09-28` vira `28 set. 2026` — como se escreve a data de acesso. */
export function dataNaReferencia(iso) {
    const [ano, mes, dia] = iso.split('-');
    const meses = ['jan.', 'fev.', 'mar.', 'abr.', 'maio', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];
    const indice = Number(mes) - 1;
    if (!ano || !dia || !meses[indice])
        return iso;
    return `${dia} ${meses[indice]} ${ano}`;
}
/**
 * As referências em ordem alfabética de sobrenome, como a norma manda na lista.
 *
 * A ordenação ignora acento e caixa: "Ávila" vem antes de "Barros", e "ávila"
 * vem no mesmo lugar que "Avila".
 */
export function referenciasEmOrdem(referencias) {
    const chave = (referencia) => {
        /* Referência livre ordena pelo próprio texto (a norma quer ordem alfabética). */
        const primeiro = referencia.tipo === 'LIVRE' ? (referencia.texto ?? '') : (referencia.autores[0] ?? '');
        const nome = referencia.tipo === 'LIVRE' ? primeiro : nomeNaReferencia(primeiro);
        const semAcento = (nome || referencia.titulo)
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase();
        return semAcento;
    };
    return [...referencias].sort((a, b) => chave(a).localeCompare(chave(b), 'pt-BR'));
}
/* ------------------------------------------------------------------ */
/* Resumo                                                              */
/* ------------------------------------------------------------------ */
/**
 * O resumo com as palavras-chave embaixo, como a NBR 6028 pede.
 *
 * A norma quer o resumo em um parágrafo, espaço simples, e as palavras-chave
 * separadas por **ponto e vírgula**, antecedidas do rótulo. Quem separou por
 * vírgula vê o texto sair corrigido.
 */
export function resumoComPalavras(resumo, palavrasChave) {
    const palavras = palavrasChave
        .flatMap((palavra) => palavra.split(/[,;]/))
        .map((palavra) => palavra.trim())
        .filter(Boolean);
    const corpo = resumo.trim();
    if (palavras.length === 0)
        return corpo;
    return `${corpo}\nPalavras-chave: ${palavras.join('; ')}.`;
}
/* ------------------------------------------------------------------ */
/* Citações no texto (NBR 10520)                                       */
/* ------------------------------------------------------------------ */
/**
 * O sobrenome de um autor, em maiúsculas, como a citação o escreve.
 *
 * Sai do mesmo lugar que a referência (`nomeNaReferencia`): se a lista imprime
 * "SOUZA, Ana", a citação no texto diz "(SOUZA, 2020)" — e não há como os dois
 * divergirem, porque é a mesma função que decide.
 */
export function sobrenomeDoAutor(nome) {
    const naReferencia = nomeNaReferencia(nome);
    const [sobrenome] = naReferencia.split(',');
    return (sobrenome ?? '').trim();
}
/** Os sobrenomes dos autores, na ordem da lista: `['SOUZA', 'LIMA']`. */
export function sobrenomesDaReferencia(referencia) {
    return referencia.autores.map(sobrenomeDoAutor).filter(Boolean);
}
/**
 * A citação no texto, no formato autor-data da NBR 10520.
 *
 * Um autor vira `(SOUZA, 2020)`; dois, `(SOUZA; LIMA, 2020)` — ponto e vírgula,
 * como a norma pede; três ou mais, `(SOUZA et al., 2020)`, que é a forma aceita
 * para não arrastar a lista inteira para dentro do parágrafo. Com a página,
 * `(SOUZA, 2020, p. 15)`.
 *
 * Sem autor não há citação: a referência escrita à mão (`LIVRE`) volta em branco,
 * e quem chama avisa em vez de inventar sobrenome — a lista de referências é o que
 * a banca confere primeiro, e adivinhar erraria em silêncio.
 */
export function citacaoAutorData(referencia, pagina) {
    const sobrenomes = referencia.autores.map(sobrenomeDoAutor).filter(Boolean);
    const ano = referencia.ano?.trim();
    if (sobrenomes.length === 0 || !ano)
        return '';
    const autores = sobrenomes.length === 1
        ? sobrenomes[0]
        : sobrenomes.length === 2
            ? `${sobrenomes[0]}; ${sobrenomes[1]}`
            : `${sobrenomes[0]} et al.`;
    const pag = pagina?.trim();
    return `(${autores}, ${ano}${pag ? `, p. ${pag}` : ''})`;
}
/**
 * O que parece citação autor-data num parágrafo: parênteses, o sobrenome em caixa
 * alta, vírgula e o ano de quatro dígitos — com a página, quando houver.
 */
const CITACAO_NO_TEXTO = /\(([^()]{2,120}?),\s*(\d{4})(?:\s*,\s*p\.\s*([^()]{1,20}?))?\)/g;
/**
 * Os sobrenomes de dentro dos parênteses — ou `null` quando não é citação.
 *
 * A conferência é **conservadora** de propósito, porque aviso a mais é pior que aviso a
 * menos: aviso errado ensina a ignorar avisos. O que a deixa conservadora é a **caixa**
 * das palavras — na ABNT o sobrenome entre parênteses sai em CAIXA ALTA, e "(ver figura 2,
 * 2020)" ou "no capítulo 3, 2020" não passam por isso. Na **APA** o sobrenome sai em caixa
 * mista ("(Silva, 2020)"), e a guarda vira: começa com maiúscula e não tem algarismo.
 */
function sobrenomesDaCitacao(parte, norma = 'ABNT') {
    const partes = parte
        .split(norma === 'APA' ? /[;&]/ : /;/)
        .flatMap((pedaco) => (norma === 'APA' ? pedaco.split(/,\s*(?=[A-ZÀ-Ý])/) : [pedaco]))
        .map((pedaco) => pedaco.trim().replace(/\s+/g, ' '))
        .filter(Boolean);
    if (partes.length === 0 || partes.length > 3)
        return null;
    const sobrenomes = [];
    for (const pedaco of partes) {
        const semEtAl = pedaco.replace(/\s+et\s+al\.?$/iu, '').trim();
        if (norma === 'APA') {
            /* Caixa mista: começa com maiúscula, e algarismo aqui é número de figura, não autor. */
            if (!/^[\p{Lu}][\p{L}\s.'’-]*$/u.test(semEtAl))
                return null;
            if (/\d/.test(semEtAl))
                return null;
        }
        else {
            /* CAIXA ALTA, como a ABNT escreve a citação entre parênteses. */
            if (!/^[\p{Lu}][\p{Lu}\s.'’-]*$/u.test(semEtAl))
                return null;
            if (!/\p{Lu}/u.test(semEtAl))
                return null;
        }
        sobrenomes.push(semEtAl);
    }
    return sobrenomes;
}
/**
 * As citações autor-data do texto, sem repetir (o mesmo autor citado dez vezes é
 * uma citação só, para efeito de conferência).
 *
 * Olha os **blocos** da seção — parágrafo, citação longa e cada item de lista, que
 * num trabalho de revisão cita tanto quanto o texto corrido. Sai daqui também que a
 * conferência vale para o documento antigo: `blocosDaSecao` monta os blocos a partir
 * dos campos antigos quando eles são tudo o que existe.
 *
 * O resumo e o abstract ficam de fora: ali não se cita, e o aviso de "citação sem
 * referência" não faz sentido nesse lugar.
 */
export function citacoesDoTexto(documento, norma = 'ABNT') {
    const citacoes = [];
    const vistas = new Set();
    for (const secao of documento.secoes) {
        for (const bloco of blocosDaSecao(secao)) {
            const trechos = bloco.tipo === 'LISTA' ? (bloco.itens ?? []) : [bloco.texto ?? ''];
            for (const trecho of trechos) {
                if (!trecho?.trim())
                    continue;
                for (const achado of trecho.matchAll(CITACAO_NO_TEXTO)) {
                    const sobrenomes = sobrenomesDaCitacao(achado[1] ?? '', norma);
                    if (!sobrenomes)
                        continue;
                    const ano = achado[2];
                    const chave = `${sobrenomes.join(';')}|${ano}`;
                    if (vistas.has(chave))
                        continue;
                    vistas.add(chave);
                    citacoes.push({ texto: achado[0], sobrenomes, ano, pagina: achado[3]?.trim() });
                }
            }
        }
    }
    return citacoes;
}
/**
 * Os números citados no texto, nas normas **numeradas** (IEEE, Vancouver).
 *
 * A citação ali é o número — `[1]` no IEEE, `(1)` no Vancouver —, e a leitura aceita as
 * duas formas. O algarismo tem de ser **um a três dígitos** para ser citação: `(2020)` é
 * ano, não número de obra, e fica de fora.
 */
export function numerosCitados(documento) {
    const numeros = new Set();
    for (const secao of documento.secoes) {
        for (const bloco of blocosDaSecao(secao)) {
            const trechos = bloco.tipo === 'LISTA' ? (bloco.itens ?? []) : [bloco.texto ?? ''];
            for (const trecho of trechos) {
                if (!trecho?.trim())
                    continue;
                for (const achado of trecho.matchAll(/\[(\d{1,3})\]|\((\d{1,3})\)/g)) {
                    const bruto = achado[1] ?? achado[2];
                    const numero = Number(bruto);
                    if (numero > 0)
                        numeros.add(numero);
                }
            }
        }
    }
    return [...numeros].sort((a, b) => a - b);
}
/**
 * Esta citação é desta referência?
 *
 * Casa pelo **primeiro sobrenome e o ano**, que é o que a citação carrega: dois
 * autores, "(SOUZA; LIMA, 2020)", pedem que a lista comece com os dois; três ou
 * mais, "(SOUZA et al., 2020)", pedem só o primeiro — a citação é um prefixo da
 * lista de autores, e não a lista inteira. Referência escrita à mão (`LIVRE`) nunca
 * casa: não dá para conferir o que a pessoa digitou sem adivinhar.
 */
export function citacaoCorresponde(citacao, referencia, norma = 'ABNT') {
    if (referencia.tipo === 'LIVRE')
        return false;
    if (referencia.ano?.trim() !== citacao.ano)
        return false;
    const sobrenomes = sobrenomesDaReferencia(referencia);
    /* Na APA o sobrenome vai em caixa mista: a comparação esquece a caixa e o acento. */
    const semCaixa = (nome) => nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const igual = (a, b) => (norma === 'APA' ? semCaixa(a) === semCaixa(b) : a === b);
    return citacao.sobrenomes.every((sobrenome, indice) => igual(sobrenomes[indice] ?? '', sobrenome));
}
/** A referência da lista que atende a esta citação — ou `null`. */
export function referenciaDaCitacao(citacao, referencias, norma = 'ABNT') {
    return (referencias.find((referencia) => citacaoCorresponde(citacao, referencia, norma)) ?? null);
}
/**
 * O que falta para o documento sair como trabalho acadêmico.
 *
 * A lista é curta e útil de propósito: só o que a banca olha primeiro e o que
 * impede a impressão. Não é revisão de texto nem de norma inteira — é o que o
 * gerador consegue conferir sozinho.
 */
export function avisosDoDocumento(documento) {
    const avisos = [];
    const { identificacao, itens } = documento;
    if (!identificacao.titulo.trim()) {
        avisos.push({ nivel: 'ATENCAO', mensagem: 'O trabalho ainda está sem título.' });
    }
    if (!identificacao.autores.some((autor) => autor.nome.trim())) {
        avisos.push({ nivel: 'ATENCAO', mensagem: 'Nenhum autor foi preenchido.' });
    }
    /*
     * A capa de um **livro** é o título com o autor: instituição e cidade são da
     * capa de trabalho acadêmico, e cobrar isso de quem escreve um romance seria
     * inventar regra que não existe.
     */
    const capaDeTrabalho = itens.includes('CAPA') && documento.modelo !== 'LIVRO';
    if (capaDeTrabalho && !identificacao.instituicao.trim()) {
        avisos.push({ nivel: 'ATENCAO', mensagem: 'A capa precisa do nome da instituição.' });
    }
    if (capaDeTrabalho && !identificacao.cidade.trim()) {
        avisos.push({ nivel: 'ATENCAO', mensagem: 'A capa precisa da cidade.' });
    }
    if (itens.includes('FOLHA_DE_ROSTO') && !identificacao.orientador?.trim()) {
        avisos.push({
            nivel: 'INFO',
            mensagem: 'A folha de rosto sai com a nota do trabalho; o nome do orientador está em branco.',
        });
    }
    if (itens.includes('RESUMO') && documento.resumo.trim().length === 0) {
        avisos.push({ nivel: 'ATENCAO', mensagem: 'O resumo está vazio.' });
    }
    if (itens.includes('RESUMO') && documento.palavrasChave.length === 0) {
        avisos.push({ nivel: 'INFO', mensagem: 'O resumo saiu sem palavras-chave.' });
    }
    if (itens.includes('ABSTRACT') && !documento.abstract.trim()) {
        avisos.push({ nivel: 'INFO', mensagem: 'O abstract está vazio — a folha sai em branco.' });
    }
    if (itens.includes('LISTA_DE_FIGURAS') && documento.figuras.length === 0) {
        avisos.push({
            nivel: 'INFO',
            mensagem: 'A lista de figuras foi marcada, mas não há figura nenhuma inserida.',
        });
    }
    /*
     * Referências só são cobradas de quem **marcou** a lista. O livro não tem
     * referências por padrão — cobrar a lista de quem escreve ficção seria o tipo
     * de aviso que ensina a pessoa a ignorar avisos.
     */
    if (itens.includes('REFERENCIAS') && documento.referencias.length === 0) {
        avisos.push({ nivel: 'ATENCAO', mensagem: 'Nenhuma referência foi cadastrada.' });
    }
    if (documento.secoes.every((secao) => !blocosDaSecao(secao).some(blocoTemTexto))) {
        avisos.push({ nivel: 'ATENCAO', mensagem: 'O texto está vazio: nenhuma seção tem parágrafo.' });
    }
    /*
     * A conferência da citação (NBR 10520), nos dois sentidos: citação que não tem
     * referência na lista e obra da lista que não é citada no texto. É o par que a
     * banca confere primeiro.
     *
     * O gerador **avisa**, nunca corrige nem completa: se o "(SILVA, 2020)" do texto
     * é aquele SILVA da lista, só quem escreveu sabe. E a referência escrita à mão
     * (`LIVRE`) fica fora dos dois avisos — não há como conferir o que a pessoa
     * digitou sem adivinhar autor e ano, e adivinhar aqui erraria em silêncio.
     */
    /*
     * A norma do documento decide como a citação se escreve no texto — e é por isso que a
     * conferência a lê pela mesma norma: "(SOUZA, 2020)" na ABNT, "(Souza, 2020)" na APA.
     */
    const norma = documento.norma ?? 'ABNT';
    const temTexto = documento.secoes.some((secao) => blocosDaSecao(secao).some(blocoTemTexto));
    /*
     * Nas normas **numeradas** a conferência é outra: a citação no texto é o número, e o que
     * se confere é se cada número tem a sua referência — e se a referência numerada aparece
     * no texto. A leitura de autor-data (`citacoesDoTexto`) ali não acharia nada, e os dois
     * avisos disparariam contra um texto certo.
     */
    if (normaNumerada(norma)) {
        const citados = numerosCitados(documento);
        const numeradas = documento.referencias.filter((referencia) => referencia.tipo !== 'LIVRE' && referencia.numeroDaLista);
        if (temTexto) {
            const semLista = citados.filter((numero) => !numeradas.some((referencia) => referencia.numeroDaLista === numero));
            if (semLista.length > 0) {
                const quais = semLista.slice(0, 3).map((numero) => `[${numero}]`).join(' ');
                avisos.push({
                    nivel: 'INFO',
                    mensagem: semLista.length === 1
                        ? `A citação ${quais} não tem referência na lista.`
                        : `${semLista.length} citações do texto não têm referência na lista: ${quais}.`,
                });
            }
            const naoCitadas = numeradas.filter((referencia) => !citados.includes(referencia.numeroDaLista));
            if (itens.includes('REFERENCIAS') && naoCitadas.length > 0) {
                const primeira = naoCitadas[0];
                const rotulo = `${primeira.titulo.trim() || 'sem título'}${primeira.ano?.trim() ? ` (${primeira.ano.trim()})` : ''}`;
                avisos.push({
                    nivel: 'INFO',
                    mensagem: naoCitadas.length === 1
                        ? `A referência [${primeira.numeroDaLista}] "${rotulo}" não é citada no texto — toda obra da lista precisa aparecer no texto.`
                        : `${naoCitadas.length} referências da lista não aparecem no texto (a primeira é [${primeira.numeroDaLista}] "${rotulo}").`,
                });
            }
        }
        const semNumero = documento.referencias.filter((referencia) => referencia.tipo !== 'LIVRE' && !referencia.numeroDaLista);
        if (itens.includes('REFERENCIAS') && semNumero.length > 0) {
            avisos.push({
                nivel: 'INFO',
                mensagem: semNumero.length === 1
                    ? `1 referência ainda não tem número: a lista desta norma é numerada, e o número é dado pela **primeira citação** no texto (na lista ela sai no fim, com um número provisório).`
                    : `${semNumero.length} referências ainda não têm número: a lista desta norma é numerada, e o número é dado pela **primeira citação** no texto (na lista elas saem no fim, com um número provisório).`,
            });
        }
    }
    const citacoes = normaNumerada(norma) ? [] : citacoesDoTexto(documento, norma);
    if (!normaNumerada(norma) && temTexto) {
        const semLista = citacoes.filter((citacao) => !referenciaDaCitacao(citacao, documento.referencias, norma));
        if (semLista.length > 0) {
            const quais = semLista.slice(0, 3).map((citacao) => citacao.texto).join(' ');
            avisos.push({
                nivel: 'INFO',
                mensagem: semLista.length === 1
                    ? `A citação ${quais} não tem referência na lista.`
                    : `${semLista.length} citações do texto não têm referência na lista: ${quais}.`,
            });
        }
        const naoCitadas = documento.referencias.filter((referencia) => referencia.tipo !== 'LIVRE' &&
            !citacoes.some((citacao) => citacaoCorresponde(citacao, referencia, norma)));
        if (itens.includes('REFERENCIAS') && naoCitadas.length > 0) {
            const primeira = naoCitadas[0];
            const rotulo = `${primeira.titulo.trim() || 'sem título'}${primeira.ano?.trim() ? ` (${primeira.ano.trim()})` : ''}`;
            avisos.push({
                nivel: 'INFO',
                mensagem: naoCitadas.length === 1
                    ? `A referência "${rotulo}" não é citada no texto — toda obra da lista precisa aparecer no texto.`
                    : `${naoCitadas.length} referências da lista não aparecem no texto (a primeira é "${rotulo}").`,
            });
        }
    }
    /*
     * Subseção sem a seção de cima. A numeração progressiva pressupõe a hierarquia —
     * "1.1" existe dentro do "1" —, e quem escreve no editor pode transformar a
     * primeira seção em subseção sem perceber. O gerador numera como se a seção de
     * cima existisse (nunca sai "0.1"); o aviso diz o que está faltando.
     */
    const niveisVistos = new Set();
    const semSecaoAcima = [];
    for (const secao of documento.secoes) {
        if (secao.nivel > 1 && !niveisVistos.has(secao.nivel - 1))
            semSecaoAcima.push(secao);
        niveisVistos.add(secao.nivel);
    }
    if (semSecaoAcima.length > 0) {
        const primeira = semSecaoAcima[0];
        avisos.push({
            nivel: 'INFO',
            mensagem: `A subseção "${primeira.titulo}" não tem a seção acima dela (${primeira.nivel === 2 ? '1' : '1.1'}). A numeração progressiva precisa da hierarquia: o gerador numerou como se ela existisse.`,
        });
    }
    /*
     * Título de subseção em caixa alta. A norma usa maiúsculas na seção **primária**
     * e, nas demais, só a primeira letra — quem escreveu tudo em maiúsculas fez isso
     * por hábito, e o gerador **avisa** em vez de reescrever: baixar a caixa à força
     * estragaria sigla e nome próprio ("IBGE", "São Paulo").
     */
    const emCaixaAltaNasSubsecoes = documento.secoes.filter((secao) => secao.nivel > 1 && tituloEmCaixaAlta(secao.titulo));
    if (emCaixaAltaNasSubsecoes.length > 0) {
        avisos.push({
            nivel: 'INFO',
            mensagem: `${emCaixaAltaNasSubsecoes.length} título(s) de subseção estão em CAIXA ALTA. A norma grafa a seção primária em maiúsculas e as demais só com a primeira letra em maiúscula — "${emCaixaAltaNasSubsecoes[0].titulo}" ficaria "${comInicialMaiuscula(emCaixaAltaNasSubsecoes[0].titulo)}".`,
        });
    }
    if (itens.includes('PAGINA_DE_ANOTACAO') && folhasDeAnotacao(documento) === 0) {
        avisos.push({
            nivel: 'INFO',
            mensagem: 'As páginas de anotação estão marcadas, mas com zero folha: nenhuma será impressa.',
        });
    }
    /*
     * Faixa de texto escolhida e deixada em branco: o papel sai com uma faixa
     * vazia. É o tipo de coisa que passa despercebida até a impressão.
     */
    const folha = folhaDoDocumento(documento);
    if ((folha.cabecalho === 'LIVRE' && !folha.textoDoCabecalho?.trim()) ||
        (folha.rodape === 'LIVRE' && !folha.textoDoRodape?.trim())) {
        avisos.push({
            nivel: 'INFO',
            mensagem: 'Uma das faixas ficou no estilo "texto que eu escrever" sem texto: ela sai vazia.',
        });
    }
    /*
     * A folha de anotação é folha de escrever: usa a folha inteira, com margem
     * própria, e sai sem cabeçalho, rodapé e número. Como o documento pode ter
     * marcado os dois, é melhor avisar do que deixar a pessoa descobrir no papel.
     */
    if (itens.includes('PAGINA_DE_ANOTACAO') &&
        folhasDeAnotacao(documento) > 0 &&
        (folha.cabecalho !== 'NENHUM' || folha.rodape !== 'NENHUM' || folha.numeracao !== 'NENHUMA')) {
        avisos.push({
            nivel: 'INFO',
            mensagem: 'As folhas de anotação usam a folha inteira e saem sem cabeçalho, rodapé e número — é folha de escrever.',
        });
    }
    return avisos;
}
/**
 * O título está todo em maiúsculas? (Só as letras contam: número e pontuação não
 * dizem nada sobre a caixa.)
 */
function tituloEmCaixaAlta(titulo) {
    const letras = titulo.replace(/[^\p{L}]/gu, '');
    return letras.length > 1 && letras === letras.toUpperCase();
}
/** O mesmo título com a primeira letra maiúscula e as demais minúsculas. */
function comInicialMaiuscula(titulo) {
    const limpo = titulo.trim();
    return limpo.charAt(0).toUpperCase() + limpo.slice(1).toLowerCase();
}
/**
 * O formato do parágrafo do documento (ausente = recuado, o de sempre).
 *
 * Existe como função porque o desenho não deve decidir sozinho: quem diz o recuo e o
 * espaço entre parágrafos é o motor, e o padrão de quem não escolheu nada é o recuado
 * — o que os documentos salvos antes deste campo já imprimiam.
 */
export function formatoDeParagrafo(documento) {
    return documento.paragrafo === 'SIMPLES' ? 'SIMPLES' : 'RECUADO';
}
/**
 * O resumo curto do documento, para a lista da área do cliente.
 */
export function resumoDoDocumento(documento) {
    return {
        titulo: documento.identificacao.titulo || 'Documento sem título',
        autores: documento.identificacao.autores
            .map((autor) => autor.nome.trim())
            .filter(Boolean)
            .join(', '),
        modelo: documento.modelo,
        papel: papelDoDocumento(documento),
        secoes: documento.secoes.length,
        figuras: documento.figuras.length,
        referencias: documento.referencias.length,
        itens: documento.itens.length,
    };
}
/** Rótulo do tipo de referência, para o desenho não depender do dicionário. */
export function tipoDeReferenciaConhecido(tipo) {
    return ['LIVRO', 'ARTIGO', 'SITE', 'CAPITULO', 'TRABALHO', 'LIVRE'].includes(tipo);
}
//# sourceMappingURL=abnt.js.map